import { useEffect, useCallback } from 'react';
import api from '../utils/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function usePushNotifications(user) {
  const subscribe = useCallback(async () => {
    if (!user) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) return;

    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        // Already subscribed — make sure backend has it
        await api.post('/push/subscribe', { subscription: existing.toJSON() }).catch(() => {});
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      await api.post('/push/subscribe', { subscription: sub.toJSON() });
    } catch (e) {
      console.warn('Push subscription failed:', e);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // Delay prompt — don't hit them the instant they log in
    const t = setTimeout(subscribe, 8000);
    return () => clearTimeout(t);
  }, [user, subscribe]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }
      await api.post('/push/unsubscribe');
    } catch (e) {
      console.warn('Unsubscribe failed:', e);
    }
  }, []);

  return { subscribe, unsubscribe };
}
